import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ActionIcon,
  Menu,
  Badge,
  Tooltip,
  Progress,
  RingProgress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconDots,
  IconRefresh,
  IconMaximize,
  IconDownload,
  IconSettings,
  IconArrowUp,
  IconArrowDown,
  IconMinus,
  IconTrendingUp,
  IconTrendingDown,
} from '@tabler/icons-react';
import { Card, Title } from '../../ui';
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface DashboardWidgetProps {
  title: string;
  subtitle?: string;
  value?: string | number;
  previousValue?: number;
  change?: number;
  changeLabel?: string;
  changePeriod?: string;
  icon?: ReactNode;
  iconColor?: string;
  progress?: number;
  progressLabel?: string;
  sparklineData?: number[];
  children?: ReactNode;
  variant?: 'stat' | 'chart' | 'list' | 'custom';
  size?: 'sm' | 'md' | 'lg';
  onRefresh?: () => void;
  onExpand?: () => void;
  onSettings?: () => void;
  onExport?: () => void;
  loading?: boolean;
  error?: string;
  lastUpdated?: Date;
  className?: string;
}

export const DashboardWidget = forwardRef<HTMLDivElement, DashboardWidgetProps>(
  (
    {
      title,
      subtitle,
      value,
      previousValue,
      change,
      changeLabel,
      changePeriod,
      icon,
      iconColor = 'blue',
      progress,
      progressLabel,
      sparklineData,
      children,
      variant = 'stat',
      size = 'md',
      onRefresh,
      onExpand,
      onSettings,
      onExport,
      loading = false,
      error,
      lastUpdated,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getChangeColor = () => {
      if (!change) return 'gray';
      return change > 0 ? 'green' : change < 0 ? 'red' : 'gray';
    };

    const getChangeIcon = () => {
      if (!change) return <IconMinus size={14} />;
      return change > 0 ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const getTrendIcon = () => {
      if (!change) return null;
      return change > 0 ? (
        <IconTrendingUp size={16} color="var(--mantine-color-green-6)" />
      ) : (
        <IconTrendingDown size={16} color="var(--mantine-color-red-6)" />
      );
    };

    const formatChange = () => {
      if (!change) return '0%';
      const sign = change > 0 ? '+' : '';
      return `${sign}${change.toFixed(1)}%`;
    };

    const renderSparkline = () => {
      if (!sparklineData || sparklineData.length === 0) return null;

      const max = Math.max(...sparklineData);
      const min = Math.min(...sparklineData);
      const range = max - min || 1;
      const height = 40;
      const width = 100;
      const points = sparklineData.map((val, i) => {
        const x = (i / (sparklineData.length - 1)) * width;
        const y = height - ((val - min) / range) * height;
        return `${x},${y}`;
      });

      return (
        <svg width={width} height={height} style={{ overflow: 'visible' }}>
          <polyline
            points={points.join(' ')}
            fill="none"
            stroke={`var(--mantine-color-${getChangeColor()}-5)`}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const padding = size === 'sm' ? 'sm' : size === 'lg' ? 'xl' : 'md';

    return (
      <FadeIn>
        <HoverLift lift={4}>
          <Card ref={ref} className={className} p={padding}>
            <Stack gap="sm">
              {/* Header */}
              <Group justify="space-between">
                <Group gap="sm">
                  {icon && (
                    <ThemeIcon size={size === 'sm' ? 'md' : 'lg'} color={iconColor} variant="light" radius="md">
                      {icon}
                    </ThemeIcon>
                  )}
                  <Box>
                    <Text size={size === 'sm' ? 'sm' : 'md'} fw={500} c="dimmed" style={accessibilityStyles}>
                      {title}
                    </Text>
                    {subtitle && (
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>
                    )}
                  </Box>
                </Group>
                {(onRefresh || onExpand || onSettings || onExport) && (
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {onRefresh && (
                        <Menu.Item leftSection={<IconRefresh size={14} />} onClick={onRefresh}>
                          Refresh
                        </Menu.Item>
                      )}
                      {onExpand && (
                        <Menu.Item leftSection={<IconMaximize size={14} />} onClick={onExpand}>
                          Expand
                        </Menu.Item>
                      )}
                      {onExport && (
                        <Menu.Item leftSection={<IconDownload size={14} />} onClick={onExport}>
                          Export
                        </Menu.Item>
                      )}
                      {onSettings && (
                        <>
                          <Menu.Divider />
                          <Menu.Item leftSection={<IconSettings size={14} />} onClick={onSettings}>
                            Settings
                          </Menu.Item>
                        </>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>

              {/* Error state */}
              {error && (
                <Text size="sm" c="red" style={accessibilityStyles}>{error}</Text>
              )}

              {/* Content based on variant */}
              {variant === 'stat' && !error && (
                <Stack gap="xs">
                  <Group justify="space-between" align="flex-end">
                    <Box>
                      <Title order={size === 'sm' ? 4 : size === 'lg' ? 1 : 2}>
                        {value}
                      </Title>
                      {change !== undefined && (
                        <Group gap={4} mt={4}>
                          <Badge
                            color={getChangeColor()}
                            variant="light"
                            size="sm"
                            leftSection={getChangeIcon()}
                          >
                            {formatChange()}
                          </Badge>
                          {changeLabel && (
                            <Text size="xs" c="dimmed" style={accessibilityStyles}>{changeLabel}</Text>
                          )}
                          {changePeriod && (
                            <Text size="xs" c="dimmed" style={accessibilityStyles}>vs {changePeriod}</Text>
                          )}
                        </Group>
                      )}
                    </Box>
                    {sparklineData && (
                      <Box>{renderSparkline()}</Box>
                    )}
                  </Group>

                  {/* Progress */}
                  {progress !== undefined && (
                    <Box>
                      <Group justify="space-between" mb={4}>
                        {progressLabel && <Text size="xs" c="dimmed" style={accessibilityStyles}>{progressLabel}</Text>}
                        <Text size="xs" fw={500} style={accessibilityStyles}>{progress}%</Text>
                      </Group>
                      <Progress value={progress} size="sm" color={iconColor} />
                    </Box>
                  )}
                </Stack>
              )}

              {variant === 'chart' && !error && children}

              {variant === 'list' && !error && children}

              {variant === 'custom' && !error && children}

              {/* Footer */}
              {lastUpdated && (
                <Text size="xs" c="dimmed" ta="right" style={accessibilityStyles}>
                  Updated {formatDate(lastUpdated)}
                </Text>
              )}
            </Stack>
          </Card>
        </HoverLift>
      </FadeIn>
    );
  }
);

DashboardWidget.displayName = 'DashboardWidget';

export default DashboardWidget;
